import {
  MessagePrimitive,
  type TextMessagePartComponent,
  useAuiState,
} from '@assistant-ui/react';
import {Pill} from '@wandb/components';
import React, {useMemo} from 'react';

import {WANDB_DATA_PART_NAME} from '../../agentChat/messageConversions';
import type {ContentPart} from '../../agentChat/store';
import {ImageThumbnail} from './attachments/ImageThumbnail';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {decodePromptForMessageDisplay} from './utils';

/** UserText renders plain text parts inside the user message bubble.
 * `decodePromptForMessageDisplay` handles legacy threads created
 * pre-prompt-parts API, which carry encoded mention markers
 * (e.g., {`run_id: "...", run_display_name: "..."}`).
 */
const UserText: TextMessagePartComponent = ({text}) => {
  const cleaned = decodePromptForMessageDisplay(text);

  if (cleaned === '') {
    return null;
  }

  return <span>{cleaned}</span>;
};

const WandbDataPart = ({data}: {data: ContentPart}) => {
  if (data.type === 'wandb-run') {
    return (
      <Pill
        icon="running-repeat"
        textValue={data.runDisplayName ?? data.runName}
      />
    );
  }
  if (data.type === 'wandb-conversation') {
    return <Pill icon="forum-chat-bubble" textValue={data.conversationId} />;
  }
  return null;
};

// Images are rendered as thumbnails above the chat bubble (see below), so suppress
// the default inline image rendering inside the bubble's part list.
const NoInlineImage = () => null;

export const UserMessage = () => {
  const nightModeRevertFilter = useNightModeRevertFilter();
  const parts = useAuiState(s => s.message.parts);

  type MessagePart = (typeof parts)[number];
  const imageParts = useMemo(
    () =>
      parts.filter(
        (p): p is Extract<MessagePart, {type: 'image'}> => p.type === 'image'
      ),
    [parts]
  );

  // Hide the chat bubble entirely for image-only messages
  const hasBubbleContent = useMemo(
    () =>
      parts.some(p =>
        p.type === 'text' ? p.text.trim().length > 0 : p.type !== 'image'
      ),
    [parts]
  );

  return (
    <MessagePrimitive.Root
      className="flex flex-col items-end gap-10 pl-24 pt-16"
      data-role="user">
      {imageParts.length > 0 && (
        <div className="flex max-w-full gap-8 overflow-x-auto [scrollbar-width:thin]">
          {imageParts.map((part, index) => (
            // A sent message's image list is immutable (never reordered or
            // mutated), and these parts carry no stable id, so an index key is
            // safe here.
            // oxlint-disable-next-line react/no-array-index-key
            <ImageThumbnail key={index} src={part.image} />
          ))}
        </div>
      )}
      {hasBubbleContent && (
        <div className="relative max-w-full break-words overflow-hidden rounded-2xl px-16 py-8 text-moon-800 text-[15px] font-normal">
          <div
            className="absolute inset-0 rounded-2xl bg-moon-100 dark:bg-moon-750"
            style={{filter: nightModeRevertFilter}}
          />
          <div className="relative whitespace-pre-wrap [&_[data-component=Pill]]:mx-2 [&_[data-component=Pill]]:align-middle">
            <MessagePrimitive.Parts
              components={{
                Text: UserText,
                Image: NoInlineImage,
                data: {
                  by_name: {
                    [WANDB_DATA_PART_NAME]: ({data}) => (
                      <WandbDataPart data={data as ContentPart} />
                    ),
                  },
                },
              }}
            />
          </div>
        </div>
      )}
    </MessagePrimitive.Root>
  );
};
